import { useEffect, useState } from 'react';
import { MAX_RETURN_RADIUS_KM, RETURN_PREFERENCE_LIMIT, type PlannedPoint, type ReturnRouting } from '../../shared/contracts.js';
import { returnRouteContext } from '../../shared/return-routing.js';
import { returnPreferenceLabel } from '../return-preference.js';
import { RangeSlider } from './ui.js';

export function ReturnRouteControls({ points, settings, preferenceValue, returnToStart, onPreferenceChange, onPreferenceCommit, onRadiusChange, onDraftChange }: {
  points: PlannedPoint[];
  settings: ReturnRouting;
  preferenceValue: number;
  returnToStart: boolean;
  onPreferenceChange: (value: number) => void;
  onPreferenceCommit: (value: number) => void;
  onRadiusChange: (radius: number) => void;
  onDraftChange: () => void;
}) {
  const [draft, setDraft] = useState(String(settings.nearStartKm));
  useEffect(() => setDraft(String(settings.nearStartKm)), [settings.nearStartKm]);
  const context = returnRouteContext(points, settings);
  const commit = () => {
    const value = draft.trim() ? Number(draft) : NaN;
    const radius = Number.isFinite(value) ? Math.max(0, Math.min(MAX_RETURN_RADIUS_KM, Math.round(value))) : settings.nearStartKm;
    setDraft(String(radius));
    onRadiusChange(radius);
  };
  return (
    <div className="return-route-controls">
      <strong className="return-route-heading">Return route</strong>
      {context.eligible ? <div className="return-preference">
        <p className="return-preference-value">{returnPreferenceLabel(preferenceValue)}</p>
        <RangeSlider value={preferenceValue} min={-RETURN_PREFERENCE_LIMIT} max={RETURN_PREFERENCE_LIMIT} label="Return road preference" valueText={returnPreferenceLabel(preferenceValue)} centered onValueChange={onPreferenceChange} onValueCommit={onPreferenceCommit} />
        <div className="range-labels return-preference-labels"><span>Same roads</span><span>Neutral</span><span>Different roads</span></div>
      </div> : null}
      {!returnToStart ? <label className="return-radius-setting">Destination within
        <input aria-label="Near-start radius in kilometres" type="number" inputMode="numeric" min={0} max={MAX_RETURN_RADIUS_KM} step={1} value={draft}
          onChange={(event) => { setDraft(event.target.value); onDraftChange(); }} onBlur={commit}
          onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
        km of start
      </label> : null}
      <p className="field-note">{context.eligible
        ? `Return begins at ${points[context.turnaroundIndex]!.label}, the farthest stop from the start. Stops stay in order; the road preference uses only the remaining time allowance.`
        : points.length < 3 ? 'Add a stop to choose a return-road preference.'
          : `The destination is ${context.endpointDistanceKm.toFixed(1)} km from the start (straight line). Increase the radius to enable return-road preferences; otherwise no return-road bias is applied.`}</p>
    </div>
  );
}
